import type { ApiSchemas } from '@rental/contracts/client'
import { parsePaperSheetNumber } from '@rental/domain'
import { readingKm } from '../handover/readiness'
import { MAX_CHARGE_CENTS } from '../handover/return-readiness'
import { parseEuroCents } from '../money'
import { romeInstant } from '../time'

/**
 * S11: a printed set typed back in, as the request the server takes and what
 * the form still lacks. The screen stays thin: what is checked here is only
 * what the form can know. The times against now and the window, their order
 * against the records, the sheet against its kit and every check of the
 * counter stay the server's, which answers in words.
 */

/** The most money lines one set takes (the API's own limit). */
export const MAX_PAPER_PAYMENTS = 10

/** Counter money only: online money is Stripe's to record. */
export const PAPER_METHODS = ['cash', 'card_terminal', 'bank_transfer'] as const
export type PaperMethod = (typeof PAPER_METHODS)[number]

export const PAPER_CHANNELS = ['portal', 'pec'] as const
export type PaperChannel = (typeof PAPER_CHANNELS)[number]

/** A day and an hour as written on the sheet: Rome's wall clock. */
export interface PaperWhen {
  date: string
  time: string
}

export interface PaperHandoverForm extends PaperWhen {
  sheet: string
  vehicleId: string
  km: string
  fuel: string
  documentsChecked: boolean
  /**
   * The person recorded for the booking. None: the hand-over is refused
   * whatever the Owner says (decision 5), so the form waits for it.
   */
  identityId: string | null
  /**
   * Q-162: the booking has a second driver whose identity is not recorded.
   * The hand-over is refused whatever the Owner says, as for the renter's.
   */
  secondIdentityMissing: boolean
  /**
   * CaRGOS's receipt, typed in the set: only when the category needs one and
   * none on file covers this car. Null otherwise.
   */
  receipt: (PaperWhen & { channel: PaperChannel | ''; number: string }) | null
  /** Checks the Owner answers for that fail (eligibility, birth date, deposit): a reason then. */
  overridden: boolean
  overrideReason: string
}

export interface PaperReturnForm extends PaperWhen {
  km: string
  fuel: string
  notes: string
}

export interface PaperPaymentRow extends PaperWhen {
  /** Rows come and go; this keeps each one's inputs its own. */
  key: number
  amount: string
  method: PaperMethod | ''
  reference: string
}

export interface PaperForm {
  /** Null: the set has no hand-over. */
  handover: PaperHandoverForm | null
  /** Null: the set has no return. */
  return: PaperReturnForm | null
  /** The km the car left with before this set (the rental's), for a return alone. */
  kmOut: number | null
  payments: readonly PaperPaymentRow[]
  /** The drawer open now, for the cash among the payments (A6). */
  drawerId: string | null
  reason: string
  code: string
}

/** What the form still lacks, in the page's order. */
export type PaperReason =
  | 'empty'
  | 'sheet'
  | 'vehicle'
  | 'identity'
  | 'secondIdentity'
  | 'documents'
  | 'handoverTime'
  | 'kmOut'
  | 'fuelOut'
  | 'receiptChannel'
  | 'receiptNumber'
  | 'receiptTime'
  | 'overrideReason'
  | 'returnTime'
  | 'kmIn'
  | 'kmBelow'
  | 'fuelIn'
  | 'paymentTime'
  | 'paymentAmount'
  | 'paymentMethod'
  | 'drawer'
  | 'reason'
  | 'code'

export type PaperSetBody = ApiSchemas['PaperSetRequest']

export interface PaperSetState {
  reasons: PaperReason[]
  ready: boolean
  /** The request, once nothing is missing. */
  body: PaperSetBody | null
}

/** A fuel level as the select holds it: eighths, 0 to 8. */
function fuelLevel(text: string): number | null {
  return /^[0-8]$/.test(text) ? Number(text) : null
}

const at = (when: PaperWhen) => romeInstant(when.date, when.time)

const OWNER_CODE = /^\d{6}$/

/** The payment's amount as the server takes it: a cent to the API's limit. */
export function paperAmount(text: string): number | null {
  const cents = parseEuroCents(text)
  return cents !== null && cents >= 1 && cents <= MAX_CHARGE_CENTS ? cents : null
}

export function paperSet(form: PaperForm): PaperSetState {
  const reasons: PaperReason[] = []
  const missing = (reason: PaperReason) => {
    if (!reasons.includes(reason)) reasons.push(reason)
  }
  const { handover, payments } = form
  const back = form.return
  if (!handover && !back && payments.length === 0) missing('empty')

  const sheet = handover ? parsePaperSheetNumber(handover.sheet) : null
  const handedOverAt = handover ? at(handover) : null
  const kmOut = handover ? readingKm(handover.km) : null
  const fuelOut = handover ? fuelLevel(handover.fuel) : null
  const receiptAt = handover?.receipt ? at(handover.receipt) : null
  if (handover) {
    if (!sheet) missing('sheet')
    if (!handover.vehicleId) missing('vehicle')
    if (!handover.identityId) missing('identity')
    if (handover.secondIdentityMissing) missing('secondIdentity')
    if (!handover.documentsChecked) missing('documents')
    if (!handedOverAt) missing('handoverTime')
    if (kmOut === null) missing('kmOut')
    if (fuelOut === null) missing('fuelOut')
    if (handover.receipt) {
      if (!handover.receipt.channel) missing('receiptChannel')
      if (!handover.receipt.number.trim()) missing('receiptNumber')
      if (!receiptAt) missing('receiptTime')
    }
    if (handover.overridden && handover.overrideReason.trim().length < 3) {
      missing('overrideReason')
    }
  }

  const returnedAt = back ? at(back) : null
  const kmIn = back ? readingKm(back.km) : null
  const fuelIn = back ? fuelLevel(back.fuel) : null
  if (back) {
    if (!returnedAt) missing('returnTime')
    if (kmIn === null) missing('kmIn')
    // Below the hand-over's reading, typed here or recorded: the server refuses it.
    const left = handover ? kmOut : form.kmOut
    if (kmIn !== null && left !== null && kmIn < left) missing('kmBelow')
    if (fuelIn === null) missing('fuelIn')
  }

  const money = payments.map((row) => ({
    at: at(row),
    amountCents: paperAmount(row.amount),
    method: row.method,
  }))
  for (const line of money) {
    if (!line.at) missing('paymentTime')
    if (line.amountCents === null) missing('paymentAmount')
    if (!line.method) missing('paymentMethod')
  }
  if (money.some((line) => line.method === 'cash') && !form.drawerId) missing('drawer')

  if (form.reason.trim().length < 3) missing('reason')
  if (!OWNER_CODE.test(form.code)) missing('code')

  if (reasons.length > 0) return { reasons, ready: false, body: null }

  const body: PaperSetBody = {
    reason: form.reason.trim(),
    code: form.code,
    payments: payments.map((row, index) => {
      const line = money[index]
      const reference = row.reference.trim()
      return {
        at: line?.at ?? '',
        method: row.method as PaperMethod,
        amountCents: line?.amountCents ?? 0,
        ...(reference ? { reference } : {}),
        ...(row.method === 'cash' && form.drawerId ? { cashSessionId: form.drawerId } : {}),
      }
    }),
  }
  if (handover && sheet && handedOverAt && kmOut !== null && fuelOut !== null) {
    // The number as printed, whatever the case, spaces or dashes it was typed with.
    body.sheet = sheet.number
    body.vehicleId = handover.vehicleId
    body.handover = {
      at: handedOverAt,
      kmOut,
      fuelOut,
      documentsChecked: true,
      ...(handover.overridden ? { override: { reason: handover.overrideReason.trim() } } : {}),
    }
    if (handover.receipt && receiptAt && handover.identityId && handover.receipt.channel) {
      body.receipt = {
        at: receiptAt,
        channel: handover.receipt.channel,
        receipt: handover.receipt.number.trim(),
        identityId: handover.identityId,
      }
    }
  }
  if (back && returnedAt && kmIn !== null && fuelIn !== null) {
    const notes = back.notes.trim()
    // No charges and no damage: the sheet has no line for them (DECISIONS S11).
    body.return = {
      at: returnedAt,
      kmIn,
      fuelIn,
      ...(notes ? { notes } : {}),
      charges: [],
      waiveExcessKm: false,
    }
  }
  return { reasons: [], ready: true, body }
}
